import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Box, CheckCircle2, Clock, DraftingCompass, MoreHorizontal, Pencil, Plus, Printer, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { PersonChip, StatCard } from '@/components/engineering/EngParts';
import { EngPrintDialog } from '@/components/engineering/EngPrintDialog';
import { PrintPhotoLightbox, PrintStatusBadge, PrintThumb } from '@/components/engineering/EngPrintsTab';
import { printViewerTarget, summarisePrints } from '@/components/engineering/engPrintUtils';
import { ModelViewerDialog, type ModelViewerTarget } from '@/components/engineering/ModelViewerDialog';
import { useAuth } from '@/app/AuthProvider';
import { useDeleteEngPrint, useEngPrints, useEngProjects, type EngPrint } from '@/features/engineering/hooks';
import { ENG_PRINT_STATUS_OPTIONS, canEditEngProject, formatPrintTime } from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatCurrency, formatDateOnly } from '@/lib/formatters';

const EMPTY: EngPrint[] = [];

/**
 * Every 3D print across all engineering projects — handy for whoever
 * runs the printers, and for spotting which parts keep failing.
 */
export function EngineeringPrintsPage() {
  const { role, profile } = useAuth();
  const { data: prints, isLoading } = useEngPrints(null);
  const { data: projects } = useEngProjects({ includeArchived: true });
  const remove = useDeleteEngPrint();
  const [dialog, setDialog] = useState<{ print: EngPrint | null } | null>(null);
  const [lightbox, setLightbox] = useState<EngPrint | null>(null);
  const [viewer, setViewer] = useState<ModelViewerTarget | null>(null);

  const rows = prints ?? EMPTY;
  const summary = useMemo(() => summarisePrints(rows), [rows]);

  // Which projects the caller may log prints for; RLS is the real gate.
  const editableProjects = useMemo(() => {
    const set = new Set<string>();
    for (const p of projects ?? []) {
      const ok = canEditEngProject({
        role,
        profileId: profile?.id,
        project: p,
        memberIds: p.eng_project_members.map((m) => m.profile_id),
      });
      if (ok) set.add(p.id);
    }
    return set;
  }, [projects, role, profile?.id]);
  const canLogAny = editableProjects.size > 0;
  const canEditPrint = useCallback(
    (print: EngPrint) => editableProjects.has(print.project_id),
    [editableProjects],
  );

  const removeAsync = remove.mutateAsync;
  const handleDelete = useCallback(
    async (print: EngPrint) => {
      if (!window.confirm(`Delete the print “${print.title}”? This cannot be undone.`)) return;
      try {
        await removeAsync({ print });
        toast.success('Print deleted.');
      } catch (err) {
        toast.error(formatError(err, 'Could not delete the print.'));
      }
    },
    [removeAsync],
  );

  const columns = useMemo<ColumnDef<EngPrint, unknown>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: 'Print',
        size: 300,
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <PrintThumb print={p} className="h-10 w-10" onClick={() => setLightbox(p)} />
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">{p.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {p.file ? `${p.file.name}${p.version ? ` · v${p.version.version_no}` : ''}` : 'No part file'}
                  {p.quantity > 1 ? ` · ×${p.quantity}` : ''}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'project',
        accessorFn: (r) => r.project?.code ?? '',
        header: 'Project',
        size: 200,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const p = row.original.project;
          if (!p) return <span className="text-muted-foreground">—</span>;
          return (
            <Link
              to={`/engineering/${p.id}?tab=prints`}
              className="block min-w-0 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
              <span className="block truncate text-sm">{p.name}</span>
            </Link>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <PrintStatusBadge status={row.original.status} />,
      },
      {
        id: 'printer',
        accessorFn: (r) => r.printer?.name ?? r.printer_name ?? '',
        header: 'Printer',
        size: 170,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const p = row.original;
          const label = p.printer?.name ?? p.printer_name;
          return label ? <span className="truncate">{label}</span> : <span className="text-muted-foreground">—</span>;
        },
      },
      {
        id: 'material',
        accessorFn: (r) => r.material ?? '',
        header: 'Material',
        size: 150,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const p = row.original;
          const label = [p.material, p.color].filter(Boolean).join(' · ');
          return label ? <span className="truncate">{label}</span> : <span className="text-muted-foreground">—</span>;
        },
      },
      {
        id: 'time',
        accessorFn: (r) => r.print_time_minutes ?? -1,
        header: 'Time',
        size: 100,
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums">{formatPrintTime(row.original.print_time_minutes)}</span>
        ),
      },
      {
        id: 'cost',
        accessorFn: (r) => r.cost_cents ?? -1,
        header: 'Cost',
        size: 100,
        cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.cost_cents)}</span>,
      },
      {
        id: 'printed_on',
        accessorFn: (r) => r.printed_on ?? '',
        header: 'Printed',
        size: 120,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm">{formatDateOnly(row.original.printed_on)}</span>
        ),
      },
      {
        id: 'printed_by',
        accessorFn: (r) => r.printed_by_profile?.full_name ?? r.printed_by_profile?.email ?? '',
        header: 'By',
        size: 160,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <PersonChip person={row.original.printed_by_profile} size="xs" />,
      },
      {
        id: 'actions',
        header: '',
        size: 56,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => {
          const p = row.original;
          const editable = canEditPrint(p);
          const target = printViewerTarget(p);
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Print actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onSelect={() => setDialog({ print: p })}>
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  {editable ? 'Edit print' : 'View details'}
                </DropdownMenuItem>
                {target && (
                  <DropdownMenuItem onSelect={() => setViewer(target)}>
                    <Box className="mr-2 h-3.5 w-3.5" />
                    View part in 3D
                  </DropdownMenuItem>
                )}
                {p.project && (
                  <DropdownMenuItem asChild>
                    <Link to={`/engineering/${p.project.id}?tab=prints`}>
                      <DraftingCompass className="mr-2 h-3.5 w-3.5" />
                      Open project
                    </Link>
                  </DropdownMenuItem>
                )}
                {editable && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => handleDelete(p)}>
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Delete print
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [canEditPrint, handleDelete],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'status', title: 'Status', options: ENG_PRINT_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
      { columnId: 'project', title: 'Project' },
      { columnId: 'printer', title: 'Printer' },
      { columnId: 'material', title: 'Material' },
      { columnId: 'printed_by', title: 'Printed by' },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Engineering"
        title="3D print log"
        meta="Every print across every project — what was printed, on which machine, how it went."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/engineering">
                <DraftingCompass className="h-4 w-4" />
                Projects
              </Link>
            </Button>
            {canLogAny && (
              <Button onClick={() => setDialog({ print: null })}>
                <Plus className="h-4 w-4" />
                Log a print
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Prints logged"
          value={summary.total}
          icon={Printer}
          hint={summary.inProgress > 0 ? `${summary.inProgress} planned or printing` : 'All projects'}
        />
        <StatCard
          label="Success rate"
          value={summary.successRate == null ? '—' : `${Math.round(summary.successRate * 100)}%`}
          icon={CheckCircle2}
          hint={summary.finished > 0 ? `${summary.successes} of ${summary.finished} finished` : 'No finished prints yet'}
        />
        <StatCard
          label="Printer time"
          value={summary.totalMinutes > 0 ? formatPrintTime(summary.totalMinutes) : '—'}
          icon={Clock}
        />
        <StatCard
          label="Material cost"
          value={formatCurrency(summary.totalCents)}
          icon={Wallet}
          hint={summary.totalGrams > 0 ? `${Math.round(summary.totalGrams).toLocaleString()} g used` : undefined}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<EngPrint, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            searchPlaceholder="Search prints by part, printer, material or notes…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[10, 25, 50, 100]}
            onRowClick={(r) => setDialog({ print: r })}
            enableColumnReordering
            enableColumnResizing
            tableId="engineering-prints"
            emptyState={
              <EmptyState
                icon={Printer}
                title="No prints logged yet"
                detail="Log prints from a project's 3D prints tab, or start one here."
                action={
                  canLogAny ? (
                    <Button onClick={() => setDialog({ print: null })}>
                      <Plus className="h-4 w-4" />
                      Log a print
                    </Button>
                  ) : undefined
                }
              />
            }
            mobileCard={{
              title: (r) => r.title,
              subtitle: (r) => (r.project ? `${r.project.code} · ${r.project.name}` : undefined),
              badges: (r) => [
                <PrintStatusBadge key="status" status={r.status} />,
                ...(r.printer?.name || r.printer_name
                  ? [
                      <Badge key="printer" variant="outline">
                        {r.printer?.name ?? r.printer_name}
                      </Badge>,
                    ]
                  : []),
              ],
              meta: (r) => [
                { label: 'Material', value: [r.material, r.color].filter(Boolean).join(' · ') || '—' },
                { label: 'Time', value: formatPrintTime(r.print_time_minutes) },
                { label: 'Cost', value: formatCurrency(r.cost_cents) },
                { label: 'Printed', value: formatDateOnly(r.printed_on) },
              ],
            }}
          />
        </CardContent>
      </Card>

      {dialog && (
        <EngPrintDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          print={dialog.print}
          canDelete={dialog.print ? canEditPrint(dialog.print) : false}
        />
      )}
      <PrintPhotoLightbox print={lightbox} onClose={() => setLightbox(null)} />
      {viewer && <ModelViewerDialog target={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
